import { useCallback, useEffect, useMemo, useState, type ComponentPropsWithoutRef } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Module } from '../lib/types';
import { getCurriculumDeck } from '../lib/api';
import { headingId } from '../lib/headingId';
import ShareableHeading from './ShareableHeading';
import { useGlossary } from '../contexts/GlossaryContext';
import { useAuth } from '../contexts/AuthContext';
import '../styles/CurriculumDeck.css';

interface CurriculumDeckProps {
  module?: Module | null;
  onNavigate?: (path: string) => void;
}

const SLIDE_COMPONENTS: Components = {
  h2: ({ children, ...props }: ComponentPropsWithoutRef<'h2'>) => (
    <ShareableHeading id={headingId(children)} as="h2" {...(props as object)}>
      {children}
    </ShareableHeading>
  ),
  h3: ({ children, ...props }: ComponentPropsWithoutRef<'h3'>) => (
    <ShareableHeading id={headingId(children)} as="h3" {...(props as object)}>
      {children}
    </ShareableHeading>
  ),
  h4: ({ children, ...props }: ComponentPropsWithoutRef<'h4'>) => (
    <ShareableHeading id={headingId(children)} as="h4" {...(props as object)}>
      {children}
    </ShareableHeading>
  ),
};

export default function CurriculumDeck({ module, onNavigate }: CurriculumDeckProps) {
  const { t } = useGlossary();
  const { user, authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deck, setDeck] = useState<Awaited<ReturnType<typeof getCurriculumDeck>> | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [outlineOpen, setOutlineOpen] = useState(true);

  const filename = module?.content_file ?? '0-deeksha.md';

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    getCurriculumDeck(filename)
      .then((payload) => {
        if (cancelled) return;
        setDeck(payload);
        setActiveIndex(0);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : t('curriculum.card.error.loadFailed'));
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [filename, t]);

  const outline = deck?.outline ?? [];
  const slides = deck?.slides ?? [];
  const activeSlide = slides[activeIndex] ?? slides[0] ?? null;

  const navigateTo = useCallback((nextIndex: number) => {
    if (!slides.length) return;
    const bounded = Math.max(0, Math.min(nextIndex, slides.length - 1));
    setActiveIndex(bounded);
    const slide = slides[bounded];
    if (slide) {
      window.history.replaceState({}, '', `#${slide.id}`);
    }
  }, [slides]);

  useEffect(() => {
    if (!slides.length) return;
    const fromHash = window.location.hash.replace('#', '');
    if (!fromHash) return;
    const index = slides.findIndex((slide) => slide.id === fromHash);
    if (index >= 0) {
      setActiveIndex(index);
    }
  }, [slides]);

  useEffect(() => {
    const onHashChange = () => {
      const fromHash = window.location.hash.replace('#', '');
      const index = slides.findIndex((slide) => slide.id === fromHash);
      if (index >= 0) {
        setActiveIndex(index);
      }
    };

    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [slides]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        navigateTo(activeIndex - 1);
      } else if (event.key === 'ArrowRight') {
        navigateTo(activeIndex + 1);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [activeIndex, navigateTo]);

  useEffect(() => {
    if (activeSlide) {
      window.history.replaceState({}, '', `#${activeSlide.id}`);
    }
  }, [activeSlide]);

  const progress = useMemo(() => {
    if (!slides.length) return 0;
    return Math.round(((activeIndex + 1) / slides.length) * 100);
  }, [activeIndex, slides.length]);

  if (authLoading) {
    return (
      <div className="curriculum-deck-loading">{t('markdown.loading')}</div>
    );
  }

  if (!user) {
    return (
      <div className="curriculum-auth-gate">
        <div className="gate-icon" aria-hidden="true">📜</div>
        <h1 className="gate-title">राजृषि शिक्षा अभ्यासक्रम</h1>
        <p className="gate-subtitle">Curriculum · Raajadharma Gurukul</p>
        <p className="gate-body">
          यह अभ्यासक्रम पंजीकृत उपयोगकर्ताओं के लिए उपलब्ध है।
          <br />
          <span className="gate-body-en">This curriculum is available to registered members.</span>
        </p>
        <button className="gate-btn" onClick={() => onNavigate?.('/profile')}>
          साइन इन करें · Sign In
        </button>
        <button className="gate-back-btn" onClick={() => onNavigate?.('/')}>
          ← Home
        </button>
      </div>
    );
  }

  if (loading) {
    return <div className="curriculum-deck-loading">{t('markdown.loading')}</div>;
  }

  if (error) {
    return (
      <div className="curriculum-deck-error">
        <div className="error-box">{error}</div>
      </div>
    );
  }

  if (!deck || !activeSlide) {
    return <div className="curriculum-deck-empty">{t('curriculum.card.error.loadFailed')}</div>;
  }

  return (
    <div className="curriculum-deck-page">
      <header className="curriculum-deck-hero">
        <div>
          <p className="curriculum-deck-eyebrow">{module?.subtitle ?? t('curriculum.card.heading.teachers')}</p>
          <h1>{module?.title ?? deck.deck_title}</h1>
          <p className="curriculum-deck-subtitle">
            {deck.deck_title} · {deck.slide_count} slides · source {deck.filename}
          </p>
        </div>
        <div className="curriculum-deck-actions">
          <button type="button" onClick={() => navigateTo(activeIndex - 1)} disabled={activeIndex === 0}>
            ←
          </button>
          <button type="button" onClick={() => navigateTo(activeIndex + 1)} disabled={activeIndex >= slides.length - 1}>
            →
          </button>
          <button type="button" onClick={() => setOutlineOpen((value) => !value)}>
            {outlineOpen ? 'Hide outline' : 'Show outline'}
          </button>
        </div>
      </header>

      <div className="curriculum-deck-progress">
        <div className="curriculum-deck-progress-bar" style={{ width: `${progress}%` }} />
      </div>

      <div className={`curriculum-deck-layout ${outlineOpen ? '' : 'curriculum-deck-layout--outline-closed'}`}>
        {outlineOpen && (
          <aside className="curriculum-deck-outline curriculum-deck-outline--open">
            <div className="curriculum-deck-outline-header">
              <strong>Outline</strong>
              <span>{activeIndex + 1}/{slides.length}</span>
            </div>
            <ol>
              {outline.map((item, index) => (
                <li key={item.id} className={index === activeIndex ? 'is-active' : ''}>
                  <button type="button" onClick={() => navigateTo(index)}>
                    <span className="curriculum-deck-outline-index">{item.index}</span>
                    <span className="curriculum-deck-outline-title">{item.title}</span>
                  </button>
                </li>
              ))}
            </ol>
            <div className="curriculum-deck-side-links">
              {onNavigate && (
                <>
                  <button type="button" onClick={() => onNavigate('/curriculum/spokensanskritgame')}>
                    {t('curriculum.highlight.title')}
                  </button>
                  <button type="button" onClick={() => onNavigate('/curriculum/navagrahchakra')}>
                    नवग्रह चक्र
                  </button>
                </>
              )}
            </div>
          </aside>
        )}

        <main className="curriculum-deck-stage">
          <article className="curriculum-deck-slide">
            <div className="curriculum-deck-slide-meta">
              <span>Slide {activeSlide.index}</span>
              <span>{activeSlide.line_count} lines</span>
              {deck.generated_at && <span>Updated {new Date(deck.generated_at).toLocaleString('hi-IN')}</span>}
            </div>
            <h2 className="curriculum-deck-slide-title">{activeSlide.title}</h2>
            <div className="curriculum-deck-slide-body">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={SLIDE_COMPONENTS}>
                {activeSlide.markdown}
              </ReactMarkdown>
            </div>
          </article>

          {module?.personas && module.personas.length > 0 && (
            <section className="curriculum-deck-personas">
              <h3>Teachers</h3>
              <div className="curriculum-deck-persona-grid">
                {module.personas.map((persona) => (
                  <div key={persona.name} className="curriculum-deck-persona">
                    <strong>{persona.name}</strong>
                    <span>{persona.role}</span>
                    <p>{persona.description}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {onNavigate && (
            <section className="curriculum-deck-handoff">
              <div className="curriculum-deck-handoff-copy">
                <div className="curriculum-deck-handoff-eyebrow">अगला चरण</div>
                <h3>दीक्षा से वाद</h3>
                <p>
                  Slide 001 पूरा करने के बाद वाद में जाएँ, जहाँ प्रोफ़ाइल और preferences तय करके Studio के लिए आधार बनता है।
                </p>
              </div>
              <div className="curriculum-deck-handoff-visual" aria-hidden="true">
                <svg viewBox="0 0 620 130" className="curriculum-deck-handoff-svg">
                  <defs>
                    <linearGradient id="curriculum-vad-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#8b4513" />
                      <stop offset="100%" stopColor="#2458a6" />
                    </linearGradient>
                  </defs>
                  <path d="M55 65 C145 18, 215 18, 290 65 S 435 112, 515 65 S 580 35, 595 65" fill="none" stroke="url(#curriculum-vad-gradient)" strokeWidth="10" strokeLinecap="round" />
                  <circle cx="55" cy="65" r="15" fill="#fff" stroke="#8b4513" strokeWidth="4" />
                  <circle cx="595" cy="65" r="15" fill="#fff" stroke="#2458a6" strokeWidth="4" />
                  <text x="55" y="98" textAnchor="middle" className="curriculum-deck-handoff-label">दीक्षा</text>
                  <text x="595" y="98" textAnchor="middle" className="curriculum-deck-handoff-label">वाद</text>
                </svg>
              </div>
              <div className="curriculum-deck-handoff-actions">
                <button type="button" onClick={() => onNavigate('/journey')}>
                  वाद खोलें
                </button>
                <button type="button" onClick={() => onNavigate('/studio')}>
                  Studio
                </button>
              </div>
            </section>
          )}
        </main>
      </div>
    </div>
  );
}
